/** Facilitator sign-in (magic link + code) and the confirm-on-POST verify page. */
import { useEffect, useState } from "preact/hooks";
import { Header, Page } from "../components/Header";
import { Button, Card, Field, Spinner } from "../components/ui";
import { useT } from "../i18n";
import { type ApiError, post } from "../lib/api";
import { navigate } from "../router";

interface RequestResponse {
  ok: boolean;
  sent: boolean;
  ttlSeconds: number;
  devLink?: string;
  devCode?: string;
}

export function HostLogin({ onSignedIn }: { onSignedIn: () => void }) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<RequestResponse | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await post<RequestResponse>("/api/auth/request", { email: email.trim().toLowerCase() });
      setSent(res);
    } catch (err) {
      setError(t("host.login.error", { message: (err as ApiError).message }));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/api/auth/verify", { email: email.trim().toLowerCase(), code: code.trim() });
      onSignedIn();
    } catch (err) {
      const codeName = (err as ApiError).code;
      setError(
        codeName === "invalidOrExpired" || codeName === "invalidRequest"
          ? t("host.login.invalid")
          : t("host.login.error", { message: (err as ApiError).message || codeName }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <Header />
      <main id="main" class="narrow entry">
        <Card class="stack">
          <h1>{t("host.login.title")}</h1>
          {!sent ? (
            <form class="stack" onSubmit={request}>
              <p class="fg-2">{t("host.login.body")}</p>
              <Field label={t("host.login.email")} htmlFor="email" error={error}>
                <input
                  id="email"
                  class="input"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
                />
              </Field>
              <Button type="submit" size="lg" block disabled={busy} icon="mail">
                {t("host.login.send")}
              </Button>
            </form>
          ) : (
            <div class="stack">
              <h2 style={{ fontSize: "var(--text-xl)" }}>{t("host.login.sent")}</h2>
              <p class="fg-2">
                {t("host.login.sentBody", { email, minutes: Math.round(sent.ttlSeconds / 60) })}
              </p>
              {sent.devLink ? (
                <div class="card signal tight small stack-sm">
                  <strong>{t("host.login.devLink")}</strong>
                  <a href={sent.devLink} class="mono" style={{ wordBreak: "break-all" }}>
                    {sent.devLink}
                  </a>
                  {sent.devCode ? <span class="mono">{sent.devCode}</span> : null}
                </div>
              ) : null}
              <form class="stack" onSubmit={verifyCode}>
                <Field label={t("host.login.codeLabel")} htmlFor="code" error={error}>
                  <input
                    id="code"
                    class="input code"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={code}
                    onInput={(e) => setCode((e.currentTarget as HTMLInputElement).value.replace(/\D/g, ""))}
                  />
                </Field>
                <Button type="submit" size="lg" block disabled={busy || code.length !== 6}>
                  {t("host.login.verifyCode")}
                </Button>
              </form>
            </div>
          )}
        </Card>
      </main>
    </Page>
  );
}

export function HostVerify({ token, onSignedIn }: { token: string | null; onSignedIn: () => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!token) setError(t("host.verify.missing"));
  }, [token, t]);
  const confirm = async () => {
    if (!token) return;
    setBusy(true);
    try {
      await post("/api/auth/verify", { token });
      onSignedIn();
      navigate("/host", true);
    } catch (err) {
      const codeName = (err as ApiError).code;
      setError(
        codeName === "invalidOrExpired" || codeName === "invalidRequest"
          ? t("host.login.invalid")
          : t("host.login.error", { message: (err as ApiError).message || codeName }),
      );
      setBusy(false);
    }
  };
  return (
    <Page>
      <Header />
      <main id="main" class="narrow entry">
        <Card class="stack">
          <h1>{t("host.verify.title")}</h1>
          <p class="fg-2">{t("host.verify.body")}</p>
          {error ? (
            <p role="alert" class="error-text">
              {error}
            </p>
          ) : null}
          {busy ? (
            <Spinner label={t("host.verify.confirming")} />
          ) : (
            <Button size="lg" block onClick={confirm} disabled={!token} icon="check">
              {t("host.verify.confirm")}
            </Button>
          )}
          {error ? (
            <Button variant="ghost" onClick={() => navigate("/host")}>
              {t("action.back")}
            </Button>
          ) : null}
        </Card>
      </main>
    </Page>
  );
}
